// Medical Scores Database - Complete Collection Part 6
// More specialized scores

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  Beaker,
  User,
  Target,
  Shield,
  Zap
} from 'lucide-react';

export const SCORES_PART_6 = {
  // GIQLI - Gastro QoL
  giqli: {
    name: 'Score GIQLI (simplifié)',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Qualité de vie liée aux troubles digestifs',
    color: 'yellow',
    fields: [
      { id: 'pain', label: 'Douleur abdominale', type: 'select', options: [
        { value: 4, label: '4 - Jamais' }, { value: 3, label: '3 - Rarement' },
        { value: 2, label: '2 - Parfois' }, { value: 1, label: '1 - Souvent' }, { value: 0, label: '0 - Tout le temps' }
      ]},
      { id: 'fullness', label: 'Sensation de plénitude', type: 'select', options: [
        { value: 4, label: '4' }, { value: 3, label: '3' }, { value: 2, label: '2' }, { value: 1, label: '1' }, { value: 0, label: '0' }
      ]},
      { id: 'bloating', label: 'Ballonnements', type: 'select', options: [
        { value: 4, label: '4' }, { value: 3, label: '3' }, { value: 2, label: '2' }, { value: 1, label: '1' }, { value: 0, label: '0' }
      ]},
      { id: 'flatulence', label: 'Flatulences', type: 'select', options: [
        { value: 4, label: '4' }, { value: 3, label: '3' }, { value: 2, label: '2' }, { value: 1, label: '1' }, { value: 0, label: '0' }
      ]},
      { id: 'heartburn', label: 'Brûlures d\'estomac', type: 'select', options: [
        { value: 4, label: '4' }, { value: 3, label: '3' }, { value: 2, label: '2' }, { value: 1, label: '1' }, { value: 0, label: '0' }
      ]},
      { id: 'nausea', label: 'Nausées', type: 'select', options: [
        { value: 4, label: '4' }, { value: 3, label: '3' }, { value: 2, label: '2' }, { value: 1, label: '1' }, { value: 0, label: '0' }
      ]},
      { id: 'bowelFrequency', label: 'Transit anormal', type: 'select', options: [
        { value: 4, label: '4' }, { value: 3, label: '3' }, { value: 2, label: '2' }, { value: 1, label: '1' }, { value: 0, label: '0' }
      ]},
      { id: 'urgency', label: 'Urgences défécatoires', type: 'select', options: [
        { value: 4, label: '4' }, { value: 3, label: '3' }, { value: 2, label: '2' }, { value: 1, label: '1' }, { value: 0, label: '0' }
      ]},
      { id: 'wellbeing', label: 'Bien-être général', type: 'select', options: [
        { value: 4, label: '4' }, { value: 3, label: '3' }, { value: 2, label: '2' }, { value: 1, label: '1' }, { value: 0, label: '0' }
      ]},
      { id: 'dailyActivities', label: 'Impact activités quotidiennes', type: 'select', options: [
        { value: 4, label: '4' }, { value: 3, label: '3' }, { value: 2, label: '2' }, { value: 1, label: '1' }, { value: 0, label: '0' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 40 };
    },
    interpret: (score) => {
      const pct = (score / 40 * 100).toFixed(0);
      if (score >= 32) return { level: 'success', text: `GIQLI ${score}/40 (${pct}%)`, detail: 'Bonne qualité de vie digestive' };
      if (score >= 20) return { level: 'warning', text: `GIQLI ${score}/40 (${pct}%)`, detail: 'Qualité de vie altérée' };
      return { level: 'danger', text: `GIQLI ${score}/40 (${pct}%)`, detail: 'Qualité de vie très altérée' };
    },
    formula: 'GIQLI simplifié: 10 items (0-4)'
  },

  // HADS (version complète)
  hadsFull: {
    name: 'HADS Complet',
    category: 'Psychiatrie',
    icon: Brain,
    description: 'Anxiété et Dépression hospitalière',
    color: 'indigo',
    fields: [
      { id: 'a1', label: 'A1. Je me sens tendu ou énervé', type: 'select', options: [
        { value: 3, label: '3 - La plupart du temps' }, { value: 2, label: '2 - Souvent' },
        { value: 1, label: '1 - De temps en temps' }, { value: 0, label: '0 - Jamais' }
      ]},
      { id: 'd1', label: 'D1. Je prends plaisir aux mêmes choses qu\'autrefois', type: 'select', options: [
        { value: 0, label: '0 - Oui, autant' }, { value: 1, label: '1 - Pas autant' },
        { value: 2, label: '2 - Peu' }, { value: 3, label: '3 - Presque plus' }
      ]},
      { id: 'a2', label: 'A2. J\'ai une sensation de peur comme si quelque chose de terrible allait m\'arriver', type: 'select', options: [
        { value: 3, label: '3 - Très nettement' }, { value: 2, label: '2 - Oui, pas trop fort' },
        { value: 1, label: '1 - Un peu' }, { value: 0, label: '0 - Pas du tout' }
      ]},
      { id: 'd2', label: 'D2. Je ris facilement et vois le bon côté des choses', type: 'select', options: [
        { value: 0, label: '0 - Autant que par le passé' }, { value: 1, label: '1 - Plus autant' },
        { value: 2, label: '2 - Vraiment moins' }, { value: 3, label: '3 - Plus du tout' }
      ]},
      { id: 'a3', label: 'A3. Je me fais du souci', type: 'select', options: [
        { value: 3, label: '3 - Très souvent' }, { value: 2, label: '2 - Assez souvent' },
        { value: 1, label: '1 - Occasionnellement' }, { value: 0, label: '0 - Très occasionnellement' }
      ]},
      { id: 'd3', label: 'D3. Je suis de bonne humeur', type: 'select', options: [
        { value: 3, label: '3 - Jamais' }, { value: 2, label: '2 - Rarement' },
        { value: 1, label: '1 - Assez souvent' }, { value: 0, label: '0 - La plupart du temps' }
      ]},
      { id: 'a4', label: 'A4. Je peux rester tranquillement assis et me sentir détendu', type: 'select', options: [
        { value: 0, label: '0 - Oui, quoi qu\'il arrive' }, { value: 1, label: '1 - Habituellement' },
        { value: 2, label: '2 - Rarement' }, { value: 3, label: '3 - Jamais' }
      ]},
      { id: 'd4', label: 'D4. J\'ai l\'impression de fonctionner au ralenti', type: 'select', options: [
        { value: 3, label: '3 - Presque toujours' }, { value: 2, label: '2 - Très souvent' },
        { value: 1, label: '1 - Parfois' }, { value: 0, label: '0 - Jamais' }
      ]},
      { id: 'a5', label: 'A5. J\'éprouve des sensations de peur et j\'ai l\'estomac noué', type: 'select', options: [
        { value: 0, label: '0 - Jamais' }, { value: 1, label: '1 - Parfois' },
        { value: 2, label: '2 - Assez souvent' }, { value: 3, label: '3 - Très souvent' }
      ]},
      { id: 'd5', label: 'D5. Je ne m\'intéresse plus à mon apparence', type: 'select', options: [
        { value: 3, label: '3 - Plus du tout' }, { value: 2, label: '2 - Je n\'y accorde pas autant d\'attention' },
        { value: 1, label: '1 - Il m\'arrive de ne pas y faire attention' }, { value: 0, label: '0 - J\'y prête autant attention' }
      ]},
      { id: 'a6', label: 'A6. J\'ai la bougeotte et n\'arrive pas à tenir en place', type: 'select', options: [
        { value: 3, label: '3 - Oui, beaucoup' }, { value: 2, label: '2 - Un peu' },
        { value: 1, label: '1 - Pas tellement' }, { value: 0, label: '0 - Pas du tout' }
      ]},
      { id: 'd6', label: 'D6. Je me réjouis d\'avance à l\'idée de faire certaines choses', type: 'select', options: [
        { value: 0, label: '0 - Autant qu\'avant' }, { value: 1, label: '1 - Un peu moins' },
        { value: 2, label: '2 - Bien moins' }, { value: 3, label: '3 - Presque jamais' }
      ]},
      { id: 'a7', label: 'A7. J\'éprouve des sensations soudaines de panique', type: 'select', options: [
        { value: 3, label: '3 - Vraiment très souvent' }, { value: 2, label: '2 - Assez souvent' },
        { value: 1, label: '1 - Pas très souvent' }, { value: 0, label: '0 - Jamais' }
      ]},
      { id: 'd7', label: 'D7. Je peux prendre plaisir à un bon livre ou à une bonne émission', type: 'select', options: [
        { value: 0, label: '0 - Souvent' }, { value: 1, label: '1 - Parfois' },
        { value: 2, label: '2 - Rarement' }, { value: 3, label: '3 - Très rarement' }
      ]}
    ],
    calculate: (values) => {
      const anxiety = (values.a1 || 0) + (values.a2 || 0) + (values.a3 || 0) + (values.a4 || 0) + 
                      (values.a5 || 0) + (values.a6 || 0) + (values.a7 || 0);
      const depression = (values.d1 || 0) + (values.d2 || 0) + (values.d3 || 0) + (values.d4 || 0) + 
                         (values.d5 || 0) + (values.d6 || 0) + (values.d7 || 0);
      return { score: anxiety + depression, max: 42, anxiety: anxiety, depression: depression };
    },
    interpret: (score, calculated) => {
      const a = calculated?.anxiety || 0;
      const d = calculated?.depression || 0;
      let detail = '';
      if (a >= 11) detail += 'Anxiété certaine. ';
      else if (a >= 8) detail += 'Anxiété douteuse. ';
      if (d >= 11) detail += 'Dépression certaine.';
      else if (d >= 8) detail += 'Dépression douteuse.';
      if (!detail) detail = 'Pas de trouble anxio-dépressif significatif';
      
      const level = (a >= 11 || d >= 11) ? 'danger' : (a >= 8 || d >= 8) ? 'warning' : 'success';
      return { level, text: `HADS A=${a} D=${d}`, detail };
    },
    formula: 'HADS: A et D ≥7 = doute, ≥11 = certain'
  },

  // HEMORR2HAGES
  hemorr2hages: {
    name: 'Score HEMORR2HAGES',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Risque hémorragique sous anticoagulant',
    color: 'rose',
    fields: [
      { id: 'hepatic', label: 'H - Insuffisance hépatique/rénale', type: 'checkbox', points: 1 },
      { id: 'ethanol', label: 'E - Alcoolisme', type: 'checkbox', points: 1 },
      { id: 'malignancy', label: 'M - Cancer', type: 'checkbox', points: 1 },
      { id: 'olderAge', label: 'O - Âge > 75 ans', type: 'checkbox', points: 1 },
      { id: 'reducedPlatelets', label: 'R - Thrombopénie/thrombopathie', type: 'checkbox', points: 1 },
      { id: 'rebleeding', label: 'R - ATCD hémorragie', type: 'checkbox', points: 2 },
      { id: 'hypertension', label: 'H - HTA non contrôlée', type: 'checkbox', points: 1 },
      { id: 'anemia', label: 'A - Anémie', type: 'checkbox', points: 1 },
      { id: 'genetic', label: 'G - Facteurs génétiques (CYP2C9)', type: 'checkbox', points: 1 },
      { id: 'excessiveFall', label: 'E - Risque de chute élevé', type: 'checkbox', points: 1 },
      { id: 'stroke', label: 'S - ATCD AVC', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.hepatic) total += 1;
      if (values.ethanol) total += 1;
      if (values.malignancy) total += 1;
      if (values.olderAge) total += 1;
      if (values.reducedPlatelets) total += 1;
      if (values.rebleeding) total += 2;
      if (values.hypertension) total += 1;
      if (values.anemia) total += 1;
      if (values.genetic) total += 1;
      if (values.excessiveFall) total += 1;
      if (values.stroke) total += 1;
      return { score: total, max: 12 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `HEMORR2HAGES ${score}`, detail: 'Faible risque hémorragique (~2.5%/an)' };
      if (score <= 3) return { level: 'warning', text: `HEMORR2HAGES ${score}`, detail: 'Risque modéré (~5%/an)' };
      return { level: 'danger', text: `HEMORR2HAGES ${score}`, detail: 'Risque élevé (~12%/an) - Balance bénéfice/risque' };
    },
    formula: 'HEMORR2HAGES: 11 items (2 pts pour Rebleeding)'
  },

  // HOMA2-IR (simplifié)
  homa: {
    name: 'HOMA-IR',
    category: 'Endocrinologie',
    icon: Scale,
    description: 'Insulinorésistance',
    color: 'amber',
    fields: [
      { id: 'glucose', label: 'Glycémie à jeun (mmol/L)', type: 'number', min: 2, max: 20, step: 0.1 },
      { id: 'insulin', label: 'Insulinémie à jeun (mUI/L)', type: 'number', min: 1, max: 100, step: 0.1 }
    ],
    calculate: (values) => {
      const glucose = values.glucose || 5;
      const insulin = values.insulin || 10;
      const homa = (glucose * insulin) / 22.5;
      return { score: homa.toFixed(2), max: '' };
    },
    interpret: (score) => {
      const h = parseFloat(score);
      if (h < 1.7) return { level: 'success', text: `HOMA-IR ${score}`, detail: 'Pas d\'insulinorésistance' };
      if (h < 2.5) return { level: 'warning', text: `HOMA-IR ${score}`, detail: 'Insulinorésistance légère' };
      return { level: 'danger', text: `HOMA-IR ${score}`, detail: 'Insulinorésistance significative' };
    },
    formula: 'HOMA-IR = (Glycémie × Insulinémie) / 22.5'
  },

  // LRINEC - Fasciite nécrosante
  lrinec: {
    name: 'Score LRINEC',
    category: 'Infectiologie',
    icon: Thermometer,
    description: 'Détection fasciite nécrosante',
    color: 'red',
    fields: [
      { id: 'crp', label: 'CRP (mg/L)', type: 'select', options: [
        { value: 0, label: '< 150 (0 pt)' },
        { value: 4, label: '≥ 150 (4 pts)' }
      ]},
      { id: 'wbc', label: 'Leucocytes (/mm³)', type: 'select', options: [
        { value: 0, label: '< 15 000 (0 pt)' },
        { value: 1, label: '15 000-25 000 (1 pt)' },
        { value: 2, label: '> 25 000 (2 pts)' }
      ]},
      { id: 'hemoglobin', label: 'Hémoglobine (g/dL)', type: 'select', options: [
        { value: 0, label: '> 13.5 (0 pt)' },
        { value: 1, label: '11-13.5 (1 pt)' },
        { value: 2, label: '< 11 (2 pts)' }
      ]},
      { id: 'sodium', label: 'Natrémie (mmol/L)', type: 'select', options: [
        { value: 0, label: '≥ 135 (0 pt)' },
        { value: 2, label: '< 135 (2 pts)' }
      ]},
      { id: 'creatinine', label: 'Créatinine (µmol/L)', type: 'select', options: [
        { value: 0, label: '≤ 141 (0 pt)' },
        { value: 2, label: '> 141 (2 pts)' }
      ]},
      { id: 'glucose', label: 'Glycémie (mmol/L)', type: 'select', options: [
        { value: 0, label: '≤ 10 (0 pt)' },
        { value: 1, label: '> 10 (1 pt)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 13 };
    },
    interpret: (score) => {
      if (score < 6) return { level: 'success', text: `LRINEC ${score}`, detail: 'Fasciite nécrosante peu probable' };
      if (score < 8) return { level: 'warning', text: `LRINEC ${score}`, detail: 'Risque intermédiaire - Imagerie recommandée' };
      return { level: 'danger', text: `LRINEC ${score}`, detail: 'Forte suspicion fasciite nécrosante - Exploration chirurgicale urgente' };
    },
    formula: 'LRINEC ≥ 6 = suspicion haute fasciite nécrosante'
  },

  // MDRD (complète)
  mdrd: {
    name: 'MDRD complète',
    category: 'Néphrologie',
    icon: Droplets,
    description: 'Estimation DFG (formule MDRD)',
    color: 'blue',
    fields: [
      { id: 'creatinine', label: 'Créatinine (µmol/L)', type: 'number', min: 20, max: 1500 },
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 110 },
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 1, label: 'Homme' },
        { value: 0.742, label: 'Femme' }
      ]},
      { id: 'ethnicity', label: 'Origine africaine', type: 'select', options: [
        { value: 1, label: 'Non' },
        { value: 1.212, label: 'Oui' }
      ]}
    ],
    calculate: (values) => {
      const cr = values.creatinine || 100;
      const crMgDl = cr / 88.4; // Conversion µmol/L en mg/dL
      const age = values.age || 50;
      const sex = values.sex || 1;
      const eth = values.ethnicity || 1;
      
      const gfr = 175 * Math.pow(crMgDl, -1.154) * Math.pow(age, -0.203) * sex * eth;
      return { score: gfr.toFixed(0), max: 150, unit: 'mL/min/1.73m²' };
    },
    interpret: (score) => {
      const gfr = parseInt(score);
      if (gfr >= 90) return { level: 'success', text: `DFG ${score}`, detail: 'Fonction rénale normale (stade 1)' };
      if (gfr >= 60) return { level: 'success', text: `DFG ${score}`, detail: 'IRC légère (stade 2)' };
      if (gfr >= 30) return { level: 'warning', text: `DFG ${score}`, detail: 'IRC modérée (stade 3)' };
      if (gfr >= 15) return { level: 'danger', text: `DFG ${score}`, detail: 'IRC sévère (stade 4) - Néphrologue' };
      return { level: 'danger', text: `DFG ${score}`, detail: 'IRC terminale (stade 5) - Dialyse/Greffe' };
    },
    formula: 'MDRD = 175 × Cr^-1.154 × Âge^-0.203 × 0.742(F) × 1.212(Afro)'
  },

  // mMRC Dyspnée
  mmrc: {
    name: 'Échelle mMRC',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Gradation dyspnée',
    color: 'sky',
    fields: [
      { id: 'grade', label: 'Grade mMRC', type: 'select', options: [
        { value: 0, label: '0 - Dyspnée pour efforts importants' },
        { value: 1, label: '1 - Dyspnée en marchant vite ou en côte légère' },
        { value: 2, label: '2 - Marche plus lentement que personnes du même âge' },
        { value: 3, label: '3 - S\'arrête après 100m ou quelques minutes' },
        { value: 4, label: '4 - Dyspnée au moindre effort, ne sort plus' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.grade || 0, max: 4 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `mMRC ${score}`, detail: 'Dyspnée légère' };
      if (score === 2) return { level: 'warning', text: `mMRC ${score}`, detail: 'Dyspnée modérée - BPCO groupe B si symptômes' };
      return { level: 'danger', text: `mMRC ${score}`, detail: 'Dyspnée sévère - Réhabilitation respiratoire' };
    },
    formula: 'mMRC 0-4: échelle dyspnée BPCO'
  },

  // OARSI - Gonarthrose
  oarsi: {
    name: 'Critères OARSI Genou',
    category: 'Rhumatologie',
    icon: Bone,
    description: 'Diagnostic gonarthrose symptomatique',
    color: 'amber',
    fields: [
      { id: 'kneePain', label: 'Douleur du genou', type: 'checkbox', points: 1 },
      { id: 'osteophytes', label: 'Ostéophytes radiographiques', type: 'checkbox', points: 1 },
      { id: 'age', label: 'Âge > 40 ans', type: 'checkbox', points: 1 },
      { id: 'stiffness', label: 'Raideur matinale < 30 min', type: 'checkbox', points: 1 },
      { id: 'crepitus', label: 'Crépitements', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      const hasPain = values.kneePain;
      const hasOsteophytes = values.osteophytes;
      const clinicalCount = (values.age ? 1 : 0) + (values.stiffness ? 1 : 0) + (values.crepitus ? 1 : 0);
      const isPositive = hasPain && (hasOsteophytes || clinicalCount >= 2);
      return { score: total, max: 5, positive: isPositive };
    },
    interpret: (score, calculated) => {
      if (calculated?.positive) {
        return { level: 'warning', text: 'OARSI positif', detail: 'Gonarthrose symptomatique probable' };
      }
      return { level: 'success', text: 'OARSI négatif', detail: 'Critères de gonarthrose non remplis' };
    },
    formula: 'OARSI: Douleur + (Ostéophytes OU ≥2 critères cliniques)'
  },

  // ORBIT - Risque hémorragique
  orbit: {
    name: 'Score ORBIT',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Risque hémorragique FA sous AOD',
    color: 'rose',
    fields: [
      { id: 'olderAge', label: 'Âge > 74 ans', type: 'checkbox', points: 1 },
      { id: 'reducedHemoglobin', label: 'Anémie (H<13, F<12 g/dL) ou ATCD anémie', type: 'checkbox', points: 2 },
      { id: 'bleedingHistory', label: 'ATCD hémorragique (GI, IC, hémorragie majeure)', type: 'checkbox', points: 2 },
      { id: 'insufficientRenal', label: 'Insuffisance rénale (DFG < 60)', type: 'checkbox', points: 1 },
      { id: 'treatmentWithAntiplatelets', label: 'Traitement antiplaquettaire associé', type: 'checkbox', points: 2 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.olderAge) total += 1;
      if (values.reducedHemoglobin) total += 2;
      if (values.bleedingHistory) total += 2;
      if (values.insufficientRenal) total += 1;
      if (values.treatmentWithAntiplatelets) total += 2;
      return { score: total, max: 8 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `ORBIT ${score}`, detail: 'Faible risque hémorragique (~2.4%/an)' };
      if (score <= 4) return { level: 'warning', text: `ORBIT ${score}`, detail: 'Risque intermédiaire (~4.7%/an)' };
      return { level: 'danger', text: `ORBIT ${score}`, detail: 'Risque élevé (~8.6%/an) - Prudence AOD' };
    },
    formula: 'ORBIT: Older, Reduced Hb, Bleeding, Insuf. rénale, Traitement AAP'
  },

  // ROME IV - Syndrome intestin irritable
  rome4Ibs: {
    name: 'Critères ROME IV (SII)',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Diagnostic syndrome de l\'intestin irritable',
    color: 'yellow',
    fields: [
      { id: 'pain', label: 'Douleur abdominale récurrente ≥1j/sem (3 derniers mois)', type: 'checkbox', points: 1 },
      { id: 'defecation', label: 'Liée à la défécation', type: 'checkbox', points: 1 },
      { id: 'frequency', label: 'Associée à modification fréquence des selles', type: 'checkbox', points: 1 },
      { id: 'form', label: 'Associée à modification forme/aspect des selles', type: 'checkbox', points: 1 },
      { id: 'duration', label: 'Début > 6 mois', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      const hasMainCriteria = values.pain;
      const associatedCriteria = (values.defecation ? 1 : 0) + (values.frequency ? 1 : 0) + (values.form ? 1 : 0);
      const hasDuration = values.duration;
      const positive = hasMainCriteria && associatedCriteria >= 2 && hasDuration;
      return { score: associatedCriteria, max: 3, positive: positive };
    },
    interpret: (score, calculated) => {
      if (calculated?.positive) {
        return { level: 'warning', text: 'ROME IV SII positif', detail: 'Critères SII remplis - Rechercher signes d\'alarme' };
      }
      return { level: 'success', text: 'ROME IV SII négatif', detail: 'Critères SII non remplis - Autres diagnostics' };
    },
    formula: 'ROME IV: Douleur + ≥2 critères associés + début >6 mois'
  },

  // SYNTAX - Score coronaires
  syntax: {
    name: 'Score SYNTAX (simplifié)',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Complexité anatomique coronarienne',
    color: 'rose',
    fields: [
      { id: 'segments', label: 'Nombre de segments atteints', type: 'select', options: [
        { value: 0, label: '0-2 segments (faible)' },
        { value: 10, label: '3-5 segments (modéré)' },
        { value: 20, label: '> 5 segments (élevé)' }
      ]},
      { id: 'leftMain', label: 'Atteinte tronc commun', type: 'checkbox', points: 5 },
      { id: 'bifurcation', label: 'Lésions de bifurcation', type: 'checkbox', points: 3 },
      { id: 'occlusion', label: 'Occlusion totale chronique', type: 'checkbox', points: 5 },
      { id: 'calcification', label: 'Calcifications sévères', type: 'checkbox', points: 2 },
      { id: 'thrombus', label: 'Thrombus visible', type: 'checkbox', points: 2 }
    ],
    calculate: (values) => {
      let total = values.segments || 0;
      if (values.leftMain) total += 5;
      if (values.bifurcation) total += 3;
      if (values.occlusion) total += 5;
      if (values.calcification) total += 2;
      if (values.thrombus) total += 2;
      return { score: total, max: 40 };
    },
    interpret: (score) => {
      if (score <= 22) return { level: 'success', text: `SYNTAX ${score}`, detail: 'Faible complexité - Angioplastie envisageable' };
      if (score <= 32) return { level: 'warning', text: `SYNTAX ${score}`, detail: 'Complexité intermédiaire - Discussion Heart Team' };
      return { level: 'danger', text: `SYNTAX ${score}`, detail: 'Haute complexité - Pontage généralement préféré' };
    },
    formula: 'SYNTAX: évaluation complexité anatomique pour revascularisation'
  }
};
